import { Spinner } from '@consigli/facade';
import { useGetBeamstarSessionsQuery, useGetBeamstarSummaryQuery } from '@consigli/hooks';
import { cn } from '@consigli/shadcn';

import { formatCount, formatTokens } from '../format';
import { LoadError } from '../load-error';
import { DailyChart, type DailySeries } from './daily-chart';
import { FailingToolsCard } from './failing-tools-card';
import { TileGroup } from './tile-group';
import { projectTiles, qualityTiles, usageTiles } from './tiles';

const TURN_SERIES: DailySeries[] = [
  {
    key: 'failed',
    label: 'Failed',
    color: 'var(--ds-color-raw-red-700)',
    value: (day) => day.failed + day.unfinished,
  },
  {
    key: 'lost',
    label: 'Connection lost',
    color: 'var(--ds-color-raw-orange-500)',
    value: (day) => day.lost,
  },
  {
    key: 'stopped',
    label: 'Stopped',
    color: 'var(--ds-color-raw-amber-300)',
    value: (day) => day.stopped,
  },
  {
    key: 'finished',
    label: 'Finished',
    color: 'var(--ds-color-raw-gray-300)',
    value: (day) => day.finished,
  },
];

const TOKEN_SERIES: DailySeries[] = [
  {
    key: 'uncached',
    label: 'Uncached input',
    color: 'var(--ds-color-raw-blue-800)',
    value: (day) => day.inputTokens - day.cachedInputTokens,
  },
  {
    key: 'cached',
    label: 'Cached input',
    color: 'var(--ds-color-raw-blue-500)',
    value: (day) => day.cachedInputTokens,
  },
  {
    key: 'output',
    label: 'Output',
    color: 'var(--ds-color-raw-pink-600)',
    value: (day) => day.outputTokens,
  },
];

interface OverviewTabProps {
  days: number;
}

export const OverviewTab = ({ days }: OverviewTabProps) => {
  const { data: summary, isFetching, error } = useGetBeamstarSummaryQuery({ days });
  // The per-project averages come from the sessions list, which the Sessions tab shares.
  const sessions = useGetBeamstarSessionsQuery({ days });
  const loadError = error ?? sessions.error;

  if (loadError) {
    return <LoadError title="Could not load the overview" error={loadError} />;
  }
  if (!summary || !sessions.data) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', (isFetching || sessions.isFetching) && 'opacity-60')}>
      <TileGroup
        title="Usage"
        tiles={[...usageTiles(summary.totals), ...projectTiles(sessions.data)]}
      />
      <TileGroup title="Quality" tiles={qualityTiles(summary.totals)} />
      <div className="grid gap-6 xl:grid-cols-2">
        <DailyChart
          title="Turns per day"
          description="By outcome, with the turns that went wrong on the baseline. Failed includes turns that never finished."
          days={summary.perDay}
          series={TURN_SERIES}
          formatTick={formatCount}
        />
        <DailyChart
          title="Tokens per day"
          description="Uncached input in dark blue, cached input in lighter blue, output in pink."
          days={summary.perDay}
          series={TOKEN_SERIES}
          formatTick={formatTokens}
        />
      </div>
      <FailingToolsCard tools={summary.failingTools} />
    </div>
  );
};
